import { Plug, Lock, AlertTriangle, Clock, Plus, Wrench } from "lucide-react";
import { useHive } from "@/store/useHive";
import { APPS } from "@/data/apps";

const accent = APPS.integrations.accent;

/** Compact desktop surface — integration fleet health at a glance. */
export function IntegrationsWidget() {
  const openApp = useHive((s) => s.openApp);
  const integrations = useHive((s) => s.integrations);
  const selectIntegration = useHive((s) => s.selectIntegration);
  const setConnectCatalog = useHive((s) => s.setConnectCatalog);

  // Fresh accounts: nothing installed yet — send them to the catalogue.
  if (integrations.length === 0) {
    return (
      <div className="w-[330px] cursor-pointer" onClick={() => openApp("integrations")}>
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-medium text-app-2">
            <Plug size={14} style={{ color: accent }} /> Integrations
          </div>
          <span className="text-[11px] text-app-2 tabular-nums">0 installed</span>
        </div>
        <div className="rounded-xl border border-dashed border-app bg-surface-2/40 p-4 text-center">
          <div className="text-sm font-semibold text-app">Nothing connected yet</div>
          <div className="mt-1 text-xs text-app-2">
            Connect GitHub, Slack, Jira and 25+ more so Hive can see and act on your work.
          </div>
          <button
            onPointerDownCapture={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              openApp("integrations");
              setConnectCatalog(true);
            }}
            className="mt-3 inline-flex items-center justify-center gap-1 rounded-lg px-3 py-1.5 text-[11px] font-semibold text-black transition hover:brightness-105"
            style={{ background: accent }}
          >
            <Plus size={12} /> Browse the catalogue
          </button>
        </div>
      </div>
    );
  }

  const connected = integrations.filter((i) => i.status === "connected").length;
  const admin = integrations.filter((i) => i.admin).length;
  const attention = integrations.filter((i) =>
    ["degraded", "needs-reauth"].includes(i.status)
  ).length;
  const pending = integrations.filter((i) => i.status === "pending").length;
  const firstIssue = integrations.find((i) =>
    ["degraded", "needs-reauth", "pending"].includes(i.status)
  );

  const stop = (e: React.PointerEvent) => e.stopPropagation();

  const cells = [
    { n: admin, l: "admin", icon: Lock, color: "#a855f7" },
    { n: attention, l: "attention", icon: AlertTriangle, color: "#f59e0b" },
    { n: pending, l: "pending", icon: Clock, color: "#0ea5e9" },
  ];

  // show a few connected app glyphs as a "fleet" strip
  const fleet = integrations.filter((i) => i.status === "connected").slice(0, 7);

  return (
    <div className="w-[330px] cursor-pointer" onClick={() => openApp("integrations")}>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-medium text-app-2">
          <Plug size={14} style={{ color: accent }} /> Integrations
        </div>
        <span className="text-[11px] text-app-2 tabular-nums">{integrations.length} available</span>
      </div>

      <div className="flex items-end gap-3">
        <div>
          <div className="text-4xl font-semibold leading-none text-app tabular-nums">{connected}</div>
          <div className="mt-1 text-[11px] text-app-2">connected</div>
        </div>
        <div className="mb-0.5 flex-1 grid grid-cols-3 gap-1.5">
          {cells.map((c) => (
            <div key={c.l} className="rounded-lg border border-app bg-surface-2/40 py-1.5 text-center">
              <div className="flex items-center justify-center gap-1 text-sm font-semibold tabular-nums" style={{ color: c.color }}>
                <c.icon size={12} /> {c.n}
              </div>
              <div className="text-[9px] uppercase tracking-wide text-app-2">{c.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* fleet strip */}
      <div className="mt-3 flex items-center gap-1.5">
        {fleet.map((i) => (
          <span
            key={i.id}
            className="grid h-7 w-7 place-items-center rounded-lg text-[11px] font-bold"
            style={{ background: `${i.color}22`, color: i.color }}
            title={i.name}
          >
            {i.glyph}
          </span>
        ))}
        <span className="ml-1 text-[11px] text-app-2">+{Math.max(0, connected - fleet.length)}</span>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-1.5">
        <button
          onPointerDownCapture={stop}
          onClick={(e) => {
            e.stopPropagation();
            openApp("integrations");
            setConnectCatalog(true);
          }}
          className="flex items-center justify-center gap-1 rounded-lg py-1.5 text-[11px] font-semibold text-black transition hover:brightness-105"
          style={{ background: accent }}
        >
          <Plus size={12} /> Connect App
        </button>
        <button
          onPointerDownCapture={stop}
          onClick={(e) => {
            e.stopPropagation();
            if (firstIssue) selectIntegration(firstIssue.id);
            openApp("integrations");
          }}
          className="flex items-center justify-center gap-1 rounded-lg border border-app bg-surface-2/60 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong"
        >
          <Wrench size={12} /> Fix Issues {attention + pending > 0 && <span className="text-amber-500">· {attention + pending}</span>}
        </button>
      </div>
    </div>
  );
}
